import { api } from '@/lib/api'
import { API_PATHS } from '@/constants/api-paths'
import type { Assignment, WorkDay, WorkDayEntry, PaginatedResponse, ApiResponse, JobAuditEntry, AssignmentJobView } from '@/types'
import {
  resolveAssignmentStatus,
} from '@/lib/assignment-status'

const emptyPaginatedResponse = <T>(): PaginatedResponse<T> => ({
  count: 0,
  next: null,
  previous: null,
  results: [],
})

const normalizePaginatedResponse = <T>(
  payload?: ApiResponse<PaginatedResponse<T>> | PaginatedResponse<T> | null
): PaginatedResponse<T> => {
  if (!payload) {
    return emptyPaginatedResponse<T>()
  }

  if ('data' in payload) {
    return payload.data ?? emptyPaginatedResponse<T>()
  }

  return payload
}

const unwrapEntityResponse = <T extends object>(
  payload?: ApiResponse<T> | T | null
): T | null => {
  if (!payload) {
    return null
  }

  if ('data' in payload) {
    return (payload.data as T | null) ?? null
  }

  return payload
}

const requireEntityResponse = <T extends object>(
  payload: ApiResponse<T> | T | null | undefined,
  entityName: string
): T => {
  const entity = unwrapEntityResponse(payload)

  if (!entity) {
    throw new Error(`Missing ${entityName} response payload.`)
  }

  return entity
}


const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

const deriveAssignmentStatus = (assignment: Partial<Assignment> & Record<string, unknown>): Assignment['status'] => {
  const rawStatus = typeof assignment.status === 'string' ? assignment.status.trim() : ''
  const resolved = resolveAssignmentStatus(rawStatus)
  if (resolved) {
    return resolved
  }

  if (assignment.worker_id === null || assignment.worker_id === undefined || assignment.worker_id === '') {
    return 'pending'
  }

  const totalPay = toNumberOrNull(assignment.total_pay)
  if (totalPay !== null && totalPay > 0) {
    return 'accepted'
  }

  return 'pending'
}

const toOptionalIsoString = (value: unknown): string | undefined => {
  if (value === null || value === undefined || value === '') {
    return undefined
  }
  if (typeof value === 'string') {
    return value
  }
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return value.toISOString()
  }
  return undefined
}

const normalizeAssignment = (assignment: Partial<Assignment> & Record<string, unknown>): Assignment => {
  const jobId = assignment.job_id ?? assignment.job
  const workerId = assignment.worker_id ?? assignment.worker
  const hasWorker = workerId !== null && workerId !== undefined && String(workerId).trim().length > 0
  const jobRecord = assignment.job && typeof assignment.job === 'object' && !Array.isArray(assignment.job)
    ? assignment.job as unknown as Record<string, unknown>
    : null
  const startDate = toOptionalIsoString(
    assignment.start_date
    ?? assignment.scheduled_start
    ?? jobRecord?.scheduled_start
    ?? jobRecord?.start_date,
  )
  const endDate = toOptionalIsoString(
    assignment.end_date
    ?? assignment.scheduled_end
    ?? jobRecord?.scheduled_end
    ?? jobRecord?.end_date,
  )

  return {
    ...(assignment as Assignment),
    id: String(assignment.id ?? ''),
    job_id: String(jobId ?? ''),
    worker_id: hasWorker ? String(workerId) : '',
    job_title: typeof assignment.job_title === 'string' ? assignment.job_title : undefined,
    job_ref: typeof assignment.job_ref === 'string' ? assignment.job_ref : undefined,
    worker_name: typeof assignment.worker_name === 'string' ? assignment.worker_name : undefined,
    worker_skill_label: typeof assignment.worker_skill_label === 'string' ? assignment.worker_skill_label : undefined,
    role_label: typeof assignment.role_label === 'string' ? assignment.role_label : undefined,
    planner_resource_label: typeof assignment.planner_resource_label === 'string' ? assignment.planner_resource_label : undefined,
    requirement_slot: typeof assignment.requirement_slot === 'number' ? assignment.requirement_slot : undefined,
    start_date: startDate,
    end_date: endDate,
    status: deriveAssignmentStatus(assignment),
  }
}

export interface AssignmentFilters {
  page?: number
  page_size?: number
  search?: string
  job_id?: string
  worker_id?: string
  work_day_id?: string
  work_date?: string
  date_from?: string
  date_to?: string
  status?: string
  kind?: 'job' | 'project' | 'event'
  unassigned?: boolean
  needs_supervisor_attention?: boolean
  ordering?: 'recently_changed' | 'recently_added' | '-updated_at' | '-created_at'
}

export interface AssignmentAuditFilters {
  page?: number
  page_size?: number
  assignment?: string
  job?: string
  work_day_entry?: string
}

export interface AssignmentWorkerInvoiceResult {
  worker_invoice_id: number | string
  worker_id?: number | string | null
  worker_amt?: number | string | null
  status?: string | number | null
}

const buildAssignmentParams = (filters: AssignmentFilters = {}) => {
  const params = new URLSearchParams()

  if (filters.page) params.append('page', String(filters.page))
  if (filters.page_size) params.append('page_size', String(filters.page_size))
  if (filters.search) params.append('search', filters.search)
  if (filters.job_id) params.append('job', filters.job_id)
  if (filters.worker_id) params.append('worker', filters.worker_id)
  if (filters.work_day_id) params.append('work_day', filters.work_day_id)
  if (filters.work_date) params.append('work_date', filters.work_date)
  if (filters.status) params.append('status', filters.status)
  if (filters.kind) params.append('kind', filters.kind)
  if (filters.date_from) params.append('date_from', filters.date_from)
  if (filters.date_to) params.append('date_to', filters.date_to)
  if (filters.unassigned) params.append('unassigned', 'true')
  if (filters.needs_supervisor_attention) params.append('needs_supervisor_attention', 'true')
  if (filters.ordering) {
    params.append(
      'ordering',
      filters.ordering === 'recently_changed'
        ? '-updated_at'
        : filters.ordering === 'recently_added'
          ? '-created_at'
          : filters.ordering,
    )
  }

  return params
}

const resolveAssignmentsListPath = (kind?: AssignmentFilters['kind']) => {
  if (kind === 'project') return API_PATHS.assignments.listProject
  if (kind === 'event') return API_PATHS.assignments.listEvent
  return API_PATHS.assignments.list
}

const resolveWorkDaysListPath = (kind?: AssignmentFilters['kind']) => {
  if (kind === 'project') return API_PATHS.workDays.listProject
  if (kind === 'event') return API_PATHS.workDays.listEvent
  return API_PATHS.workDays.list
}

const resolveWorkDayEntriesListPath = (kind?: AssignmentFilters['kind']) => {
  if (kind === 'project') return API_PATHS.workDayEntries.listProject
  if (kind === 'event') return API_PATHS.workDayEntries.listEvent
  return API_PATHS.workDayEntries.list
}

export const assignmentsApi = {
  // Assignments
  getAssignments: async (filters: AssignmentFilters = {}, request?: { signal?: AbortSignal }): Promise<PaginatedResponse<Assignment>> => {
    const params = buildAssignmentParams(filters)
    const listPath = resolveAssignmentsListPath(filters.kind)

    const response = await api.get<ApiResponse<PaginatedResponse<Assignment>> | PaginatedResponse<Assignment>>(
      listPath,
      {
        params,
        skipPostIdHeader: true,
        signal: request?.signal,
      } as any,
    )
    const normalized = normalizePaginatedResponse(response.data)
    return {
      ...normalized,
      results: normalized.results.map((assignment) => normalizeAssignment(assignment as Partial<Assignment> & Record<string, unknown>)),
    }
  },

  getAssignment: async (id: string): Promise<Assignment> => {
    const { data } = await api.get<ApiResponse<Assignment> | Assignment>(API_PATHS.assignments.detail(id))
    return normalizeAssignment((unwrapEntityResponse(data) ?? {}) as Partial<Assignment> & Record<string, unknown>)
  },

  getAssignmentJobView: async (id: string): Promise<AssignmentJobView> => {
    const { data } = await api.get<ApiResponse<AssignmentJobView> | AssignmentJobView>(API_PATHS.assignments.jobView(id))
    const payload = unwrapEntityResponse(data)
    if (!payload) {
      throw new Error('Missing assignment job view payload.')
    }
    return payload
  },

  createAssignment: async (assignmentData: Partial<Assignment>): Promise<Assignment> => {
    const { data } = await api.post<ApiResponse<Assignment> | Assignment>(API_PATHS.assignments.create, assignmentData)
    return normalizeAssignment((unwrapEntityResponse(data) ?? {}) as Partial<Assignment> & Record<string, unknown>)
  },

  updateAssignment: async (id: string, assignmentData: Partial<Assignment> & { reason?: string }): Promise<Assignment> => {
    const { data } = await api.post<ApiResponse<Assignment> | Assignment>(API_PATHS.assignments.update(id), assignmentData)
    return normalizeAssignment((unwrapEntityResponse(data) ?? {}) as Partial<Assignment> & Record<string, unknown>)
  },

  deleteAssignment: async (id: string) => {
    await api.delete(API_PATHS.assignments.delete(id))
  },

  generateAssignmentWorkerInvoice: async (id: string): Promise<AssignmentWorkerInvoiceResult> => {
    const { data } = await api.post<ApiResponse<AssignmentWorkerInvoiceResult> | AssignmentWorkerInvoiceResult>(
      API_PATHS.assignments.generateWorkerInvoice(id),
      {},
    )
    return requireEntityResponse(data, 'worker invoice')
  },

  getAssignmentAudits: async (filters: AssignmentAuditFilters = {}): Promise<PaginatedResponse<JobAuditEntry>> => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.assignment) params.append('assignment', filters.assignment)
    if (filters.job) params.append('job', filters.job)
    if (filters.work_day_entry) params.append('work_day_entry', filters.work_day_entry)

    const response = await api.get<ApiResponse<PaginatedResponse<JobAuditEntry>> | PaginatedResponse<JobAuditEntry>>(API_PATHS.audits.list, { params })
    return normalizePaginatedResponse(response.data)
  },

  getWorkDayEntryAudits: async (
    entryId: string,
    filters: Omit<AssignmentAuditFilters, 'work_day_entry'> = {},
  ): Promise<PaginatedResponse<JobAuditEntry>> => {
    const params = new URLSearchParams()
    if (filters.page) params.append('page', String(filters.page))
    if (filters.page_size) params.append('page_size', String(filters.page_size))
    if (filters.assignment) params.append('assignment', filters.assignment)
    if (filters.job) params.append('job', filters.job)
    params.append('work_day_entry', entryId)

    const response = await api.get<ApiResponse<PaginatedResponse<JobAuditEntry>> | PaginatedResponse<JobAuditEntry>>(
      API_PATHS.audits.list,
      { params },
    )
    return normalizePaginatedResponse(response.data)
  },

  // WorkDays
  getWorkDays: async (filters: AssignmentFilters = {}): Promise<PaginatedResponse<WorkDay>> => {
    const params = buildAssignmentParams(filters)
    const listPath = resolveWorkDaysListPath(filters.kind)

    const response = await api.get<ApiResponse<PaginatedResponse<WorkDay>> | PaginatedResponse<WorkDay>>(listPath, { params })
    return normalizePaginatedResponse(response.data)
  },

  createWorkDay: async (workDayData: Partial<WorkDay>): Promise<WorkDay> => {
    const { data } = await api.post<ApiResponse<WorkDay> | WorkDay>(API_PATHS.workDays.create, workDayData)
    return requireEntityResponse(data, 'work day')
  },

  updateWorkDay: async (id: string, workDayData: Partial<WorkDay>): Promise<WorkDay> => {
    const { data } = await api.post<ApiResponse<WorkDay> | WorkDay>(API_PATHS.workDays.update(id), workDayData)
    return requireEntityResponse(data, 'work day')
  },

  syncWorkDayAssignmentEntries: async (id: string): Promise<{ id: string | number; created_entries: number }> => {
    const { data } = await api.post<ApiResponse<{ id: string | number; created_entries: number }> | { id: string | number; created_entries: number }>(
      API_PATHS.workDays.syncAssignmentEntries(id),
      {},
    )
    return requireEntityResponse(data, 'work day entry sync')
  },

  deleteWorkDay: async (id: string) => {
    await api.delete(API_PATHS.workDays.delete(id))
  },

  // WorkDay Entries
  getWorkDayEntries: async (filters: AssignmentFilters = {}, request?: { signal?: AbortSignal }): Promise<PaginatedResponse<WorkDayEntry>> => {
    const params = buildAssignmentParams(filters)
    const listPath = resolveWorkDayEntriesListPath(filters.kind)

    const response = await api.get<ApiResponse<PaginatedResponse<WorkDayEntry>> | PaginatedResponse<WorkDayEntry>>(listPath, { params, signal: request?.signal })
    return normalizePaginatedResponse(response.data)
  },

  getWorkDayEntry: async (id: string): Promise<WorkDayEntry> => {
    const { data } = await api.get<ApiResponse<WorkDayEntry> | WorkDayEntry>(API_PATHS.workDayEntries.detail(id))
    return requireEntityResponse(data, 'work day entry')
  },

  createWorkDayEntry: async (entryData: Partial<WorkDayEntry>): Promise<WorkDayEntry> => {
    const { data } = await api.post<ApiResponse<WorkDayEntry> | WorkDayEntry>(API_PATHS.workDayEntries.create, entryData)
    return requireEntityResponse(data, 'work day entry')
  },

  updateWorkDayEntry: async (
    id: string,
    entryData: Partial<WorkDayEntry>,
    options?: { focus?: 'pay' | 'review' },
  ): Promise<WorkDayEntry> => {
    const params = options?.focus ? { focus: options.focus } : undefined
    const { data } = await api.post<ApiResponse<WorkDayEntry> | WorkDayEntry>(
      API_PATHS.workDayEntries.update(id),
      entryData,
      { params },
    )
    return requireEntityResponse(data, 'work day entry')
  },

  setWorkDayEntryStatus: async (
    id: string,
    status: string,
    options?: {
      assignmentId?: string | number | null
      checkIn?: string | Date | null
      checkOut?: string | Date | null
      reason?: string | null
    },
  ): Promise<WorkDayEntry> => {
    const payload: {
      to_status: string
      assignment_id?: string
      check_in?: string
      check_out?: string
      reason?: string
    } = { to_status: status }
    if (options?.assignmentId) {
      payload.assignment_id = String(options.assignmentId)
    }
    const checkInIso = toOptionalIsoString(options?.checkIn)
    const checkOutIso = toOptionalIsoString(options?.checkOut)
    if (checkInIso) {
      payload.check_in = checkInIso
    }
    if (checkOutIso) {
      payload.check_out = checkOutIso
    }
    if (options?.reason?.trim()) {
      payload.reason = options.reason.trim()
    }
    const { data } = await api.post<ApiResponse<WorkDayEntry> | WorkDayEntry>(
      API_PATHS.workDayEntries.setStatus(id),
      payload,
    )
    return requireEntityResponse(data, 'work day entry')
  },

  clearWorkDayEntryAttention: async (id: string): Promise<WorkDayEntry> => {
    const { data } = await api.post<ApiResponse<WorkDayEntry> | WorkDayEntry>(
      API_PATHS.workDayEntries.clearAttention(id),
      {},
    )
    return requireEntityResponse(data, 'work day entry')
  },

  deleteWorkDayEntry: async (id: string) => {
    await api.delete(API_PATHS.workDayEntries.delete(id))
  },
}
